import i18next from 'i18next';
import React, { useState, useEffect } from 'react';

import { getAllLanguages } from '@services/client';

import { ListView } from '@commons/utils/styledLibraryComponents';
import { showErrorMessage } from '@actions/messageconfirmation';

import { connect } from 'react-redux';
import { getLanguageColumns, rowLabels } from '../utils';
import { ListViewContainer, ListViewWrapper } from '../styledComponents';
import { TotalRow } from './TotalRow';

const LABEL_COLUMN_MIN_WIDTH = 200;
const LANGUAGE_COLUMN_MIN_WIDTH = 150;

const TranslationsTable = ({
  showErrorMessage,
  translationsProgress,
  isLoadingProgress,
  fetchTranslationsProgress,
}) => {
  const [allLanguages, setAllLanguages] = useState([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);

  const getLanguages = async () => {
    setIsLoadingLanguages(true);
    try {
      const languages = await getAllLanguages();
      setAllLanguages(languages);
    } catch {
      showErrorMessage(i18next.t('USERS.DETAILS.FETCH_CLIENT_LANGUAGES_ERROR'));
      setAllLanguages([]);
    } finally {
      setIsLoadingLanguages(false);
    }
  };

  useEffect(() => {
    getLanguages();
  }, []);

  useEffect(() => {
    fetchTranslationsProgress();
  }, []);

  const languageIds = Object.keys(translationsProgress);
  const availableLanguages = allLanguages.filter(({ id }) => languageIds.includes(id));
  const availableLanguageIds = availableLanguages.map(({ id }) => id);

  const formattedRows = rowLabels.map((row) => {
    const languageValues = availableLanguageIds.reduce((acc, filteredLanguagesCodes) => {
      const langData = translationsProgress[filteredLanguagesCodes]?.[row.id];
      const translated = langData?.translated ?? 0;
      const total = langData?.total ?? 0;

      return {
        ...acc,
        [filteredLanguagesCodes]: `${translated} | ${total}`,
      };
    }, {});

    return { ...row, ...languageValues };
  });

  const totalsByLanguage = availableLanguageIds.reduce(
    (acc, languageCode) => ({
      ...acc,
      [languageCode]: translationsProgress[languageCode]?.translationPercentage ?? 0,
    }),
    {},
  );

  const columns = [
    {
      id: 'label',
      name: i18next.t('ADMIN.TRANSLATIONS.TRANSLATIONS_TABLE.TRANSLATIONS_TABLE_TITLE'),
      propertyKey: 'label',
      disableSort: true,
      minWidth: LABEL_COLUMN_MIN_WIDTH,
    },
    ...getLanguageColumns(availableLanguages, { minWidth: LANGUAGE_COLUMN_MIN_WIDTH }),
  ];

  const listMinWidth =
    LABEL_COLUMN_MIN_WIDTH + availableLanguageIds.length * LANGUAGE_COLUMN_MIN_WIDTH;

  return (
    <ListViewContainer>
      <ListViewWrapper minWidth={listMinWidth}>
        <ListView
          columns={columns}
          data={formattedRows}
          isLoading={isLoadingLanguages || isLoadingProgress}
          minWidth={listMinWidth}
          disableFooter
          disableHorizontalScroll
          hideSearchbar
          // to keep the same row order
          onQueryParamsChange={() => {}}
        />
      </ListViewWrapper>
      {!isLoadingLanguages && !isLoadingProgress && (
        <TotalRow columns={columns} minWidth={listMinWidth} totalsByLanguage={totalsByLanguage} />
      )}
    </ListViewContainer>
  );
};

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (msg) => dispatch(showErrorMessage(msg)),
});

export default connect(null, mapDispatchToProps)(TranslationsTable);
